/**
 * AdhocClientMasterDialog — pick, add, edit or delete an adhoc client master.
 *
 * Rendered on demand from AddEditAdhocLoadDialog. Fully isolated from the
 * existing SVE/Balaji client flow — only talks to /api/adhoc-clients.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Card } from '../../ui/card';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const emptyForm = {
  name: '', address: '', gstin: '', state: '', rate: '', invoice_style: 'balaji',
};

export function AdhocClientMasterDialog({ open, onClose, onSelect }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // {id, ...form} or 'new' or null
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchClients = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/adhoc-clients`, { headers: authHeaders() });
      setClients(r.data || []);
    } catch (e) {
      toast.error('Failed to load adhoc clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) { fetchClients(); setEditing(null); setForm(emptyForm); } }, [open]);

  const startAdd = () => { setEditing('new'); setForm(emptyForm); };
  const startEdit = (c) => {
    setEditing(c.id);
    setForm({
      name: c.name, address: c.address, gstin: c.gstin,
      state: c.state, rate: String(c.rate), invoice_style: c.invoice_style,
    });
  };
  const cancelEdit = () => { setEditing(null); setForm(emptyForm); };

  const validate = () => {
    if (!form.name.trim()) return 'Name is required';
    if (!form.address.trim()) return 'Address is required';
    if (!form.gstin.trim()) return 'GSTIN is required';
    if (!form.state.trim()) return 'State is required';
    const rate = parseFloat(form.rate);
    if (!rate || rate <= 0) return 'Rate must be > 0';
    if (!['balaji', 'sve'].includes(form.invoice_style)) return 'Pick an invoice style';
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = { ...form, rate: parseFloat(form.rate) };
      if (editing === 'new') {
        await axios.post(`${API}/adhoc-clients`, payload, { headers: authHeaders() });
        toast.success('Adhoc client added');
      } else {
        await axios.put(`${API}/adhoc-clients/${editing}`, payload, { headers: authHeaders() });
        toast.success('Adhoc client updated');
      }
      await fetchClients();
      setEditing(null);
      setForm(emptyForm);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete adhoc client "${c.name}"? Historical loads keep their snapshotted details.`)) return;
    try {
      await axios.delete(`${API}/adhoc-clients/${c.id}`, { headers: authHeaders() });
      toast.success('Deleted');
      fetchClients();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="adhoc-client-master-dialog">
        <DialogHeader>
          <DialogTitle>Adhoc Clients</DialogTitle>
        </DialogHeader>

        {/* Add / Edit form */}
        {editing !== null ? (
          <Card className="p-4 border-slate-300 bg-slate-50 space-y-3">
            <div className="text-sm font-semibold text-slate-700">
              {editing === 'new' ? 'New Adhoc Client' : 'Edit Adhoc Client'}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Client Name *</Label>
                <Input data-testid="adhoc-client-name" value={form.name}
                       onChange={(e) => setForm({ ...form, name: e.target.value })}
                       placeholder="ACME Foundry Ltd" />
              </div>
              <div>
                <Label className="text-xs">GSTIN *</Label>
                <Input data-testid="adhoc-client-gstin" value={form.gstin}
                       onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
                       placeholder="29ABCDE1234F1Z5" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Address *</Label>
                <Input data-testid="adhoc-client-address" value={form.address}
                       onChange={(e) => setForm({ ...form, address: e.target.value })}
                       placeholder="Plot no., Industrial Area, City" />
              </div>
              <div>
                <Label className="text-xs">State *</Label>
                <Input data-testid="adhoc-client-state" value={form.state}
                       onChange={(e) => setForm({ ...form, state: e.target.value })}
                       placeholder="Karnataka" />
              </div>
              <div>
                <Label className="text-xs">Default Rate (₹/kg) *</Label>
                <Input data-testid="adhoc-client-rate" type="number" step="0.01" value={form.rate}
                       onChange={(e) => setForm({ ...form, rate: e.target.value })}
                       placeholder="4.50" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Update-Load Style *</Label>
                <Select value={form.invoice_style}
                        onValueChange={(v) => setForm({ ...form, invoice_style: v })}>
                  <SelectTrigger data-testid="adhoc-client-invoice-style"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="balaji">Like Balaji — updating a delivered load regenerates a new invoice</SelectItem>
                    <SelectItem value="sve">Like SVE — weight adjustments captured for a monthly summary invoice</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={cancelEdit} data-testid="adhoc-client-cancel">
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button onClick={save} disabled={saving} data-testid="adhoc-client-save">
                <Check className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </Card>
        ) : (
          <Button onClick={startAdd} className="w-full sm:w-auto" data-testid="adhoc-client-add-new">
            <Plus className="w-4 h-4 mr-1" /> Add New Adhoc Client
          </Button>
        )}

        {/* Existing clients list */}
        <div className="space-y-2 mt-2" data-testid="adhoc-client-list">
          {loading && <div className="text-sm text-slate-500">Loading…</div>}
          {!loading && clients.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-4">
              No adhoc clients yet. Add one to get started.
            </div>
          )}
          {clients.map((c) => (
            <Card key={c.id} className="p-3 flex flex-col sm:flex-row sm:items-center gap-2 hover:bg-slate-50 transition"
                  data-testid={`adhoc-client-row-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{c.name}</div>
                <div className="text-xs text-slate-500 truncate">
                  {c.gstin} · {c.state} · ₹{c.rate}/kg · <span className="uppercase">{c.invoice_style}</span>-style
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <Button size="sm" onClick={() => onSelect(c)}
                        data-testid={`adhoc-client-use-${c.id}`}>Use</Button>
                <Button size="sm" variant="outline" onClick={() => startEdit(c)}
                        data-testid={`adhoc-client-edit-${c.id}`}><Pencil className="w-3 h-3" /></Button>
                <Button size="sm" variant="outline" onClick={() => remove(c)}
                        data-testid={`adhoc-client-delete-${c.id}`}
                        className="text-red-600 hover:text-red-700"><Trash2 className="w-3 h-3" /></Button>
              </div>
            </Card>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="adhoc-client-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
